import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Generic hook for managing localStorage state.
 *
 * The returned setter is **referentially stable** (identity never changes),
 * which is critical for consumers that pass it into effect dependency arrays
 * (e.g. NostrSync's `updateConfig`). This is achieved by using the functional
 * form of `setState` and keeping the serializer in a ref.
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  serializer?: {
    serialize: (value: T) => string;
    deserialize: (value: string) => T;
  }
) {
  const serialize = serializer?.serialize || JSON.stringify;
  const deserialize = serializer?.deserialize || JSON.parse;

  // Keep serializer in a ref so the stable `setValue` callback always uses
  // the latest serializer without needing it in the dependency array.
  const serializeRef = useRef(serialize);
  serializeRef.current = serialize;
  const keyRef = useRef(key);
  keyRef.current = key;

  const [state, setState] = useState<T>(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? deserialize(item) : defaultValue;
    } catch (error) {
      console.warn(`Failed to load ${key} from localStorage:`, error);
      return defaultValue;
    }
  });

  // Stable setter — never changes identity across renders.
  const setValue = useCallback((value: T | ((prev: T) => T)) => {
    setState((prev) => {
      try {
        const valueToStore = value instanceof Function ? value(prev) : value;
        localStorage.setItem(keyRef.current, serializeRef.current(valueToStore));
        return valueToStore;
      } catch (error) {
        console.warn(`Failed to save ${keyRef.current} to localStorage:`, error);
        return prev;
      }
    });
  }, []);

  // Sync with localStorage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setState(deserialize(e.newValue));
        } catch (error) {
          console.warn(`Failed to sync ${key} from localStorage:`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, deserialize]);

  return [state, setValue] as const;
}